import { useEffect, useState } from "react";
import { listTeams, createTeam, updateTeam, deleteTeam } from "@/lib/api";
import { Plus, X, Pencil, Trash2, Users, Save, Hash } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const ROLE_OPTIONS = [
  { value: "batter", label: "Batter" },
  { value: "bowler", label: "Bowler" },
  { value: "all_rounder", label: "All-rounder" },
  { value: "wicket_keeper", label: "Wicket-keeper" },
];

const STYLE_OPTIONS = [
  { value: "right_hand", label: "Right Hand" },
  { value: "left_hand", label: "Left Hand" },
];

const blankPlayer = () => ({ name: "", role: "", jersey_number: "", batting_style: "" });

export default function Teams() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canManage = user?.role === "admin" || user?.role === "scorer";
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {id?, name, players}
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const t = await listTeams();
      setTeams(t);
    } catch {
      toast.error("Failed to load teams");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const startCreate = () => setEditing({ id: null, name: "", players: [blankPlayer(), blankPlayer()] });
  const startEdit = (t) => setEditing({
    id: t.id,
    name: t.name,
    players: (t.players?.length ? t.players : [blankPlayer()]).map((p) => ({
      name: p.name || "",
      role: p.role || "",
      jersey_number: p.jersey_number || "",
      batting_style: p.batting_style || "",
    })),
  });

  const save = async () => {
    const cleanPlayers = editing.players
      .map((p) => ({
        name: p.name.trim(),
        role: p.role || null,
        jersey_number: (p.jersey_number || "").trim() || null,
        batting_style: p.batting_style || null,
      }))
      .filter((p) => p.name);
    if (!editing.name.trim()) {
      toast.error("Team name is required");
      return;
    }
    if (cleanPlayers.length < 2) {
      toast.error("Add at least 2 players");
      return;
    }
    try {
      setSaving(true);
      const payload = { name: editing.name.trim(), players: cleanPlayers };
      if (editing.id) {
        await updateTeam(editing.id, payload);
        toast.success("Team updated");
      } else {
        await createTeam(payload);
        toast.success("Team saved");
      }
      setEditing(null);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    if (!confirm(`Delete team "${t.name}"? This cannot be undone.`)) return;
    try {
      await deleteTeam(t.id);
      toast.success("Team deleted");
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10" data-testid="teams-page">
      <div className="flex items-start justify-between gap-4 mb-8 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-primary font-bold">Library</p>
          <h1 className="font-display font-black uppercase text-4xl sm:text-5xl tracking-tight mt-2">
            Teams &amp; Players
          </h1>
          <p className="text-muted-foreground text-sm mt-2 max-w-xl">
            Save your squads once and reuse them when starting matches. Shared across all users.
          </p>
        </div>
        {canManage && !editing && (
          <button
            onClick={startCreate}
            data-testid="new-team-btn"
            className="tactical-btn bg-primary text-primary-foreground hover:bg-[#C9E620] px-5 py-3 inline-flex items-center gap-2 text-xs"
          >
            <Plus className="w-4 h-4" strokeWidth={3} /> New Team
          </button>
        )}
      </div>

      {editing && (
        <TeamEditor
          value={editing}
          onChange={setEditing}
          onSave={save}
          onCancel={() => setEditing(null)}
          saving={saving}
        />
      )}

      {loading ? (
        <p className="text-muted-foreground uppercase tracking-[0.3em] text-xs">Loading...</p>
      ) : teams.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center">
          <Users className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground uppercase text-xs tracking-[0.2em] mb-4">No saved teams yet</p>
          {canManage && (
            <button
              onClick={startCreate}
              data-testid="empty-new-team-btn"
              className="tactical-btn bg-primary text-primary-foreground px-6 py-3 inline-flex items-center text-sm gap-2"
            >
              <Plus className="w-4 h-4" strokeWidth={3} /> Create your first team
            </button>
          )}
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {teams.map((t) => {
            const ownerCanEdit = canManage && (isAdmin || t.created_by === user?.id);
            return (
              <div key={t.id} className="border border-border bg-card p-5" data-testid={`team-card-${t.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <h2 className="font-display font-black uppercase text-xl tracking-tight truncate">{t.name}</h2>
                    <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground font-bold">
                      {t.players.length} {t.players.length === 1 ? "player" : "players"}
                      {t.created_by_name && <> · By {t.created_by_name}</>}
                    </p>
                  </div>
                  {ownerCanEdit && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => startEdit(t)}
                        data-testid={`edit-team-${t.id}`}
                        className="p-2 text-muted-foreground hover:text-primary border border-border hover:border-primary transition-colors"
                        aria-label="Edit"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => remove(t)}
                        data-testid={`delete-team-${t.id}`}
                        className="p-2 text-muted-foreground hover:text-destructive border border-border hover:border-destructive transition-colors"
                        aria-label="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
                <ul className="space-y-1.5">
                  {t.players.map((p, i) => (
                    <li key={p.id || `${t.id}-${i}`} className="flex items-center justify-between gap-2 text-sm border-b border-border last:border-0 pb-1.5 last:pb-0">
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="font-mono text-[10px] text-muted-foreground w-5 stat-tabular">{i + 1}.</span>
                        <span className="font-display uppercase font-bold tracking-tight truncate">{p.name}</span>
                        {p.jersey_number && (
                          <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground font-mono">
                            <Hash className="w-2.5 h-2.5" />{p.jersey_number}
                          </span>
                        )}
                      </span>
                      <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold shrink-0">
                        {p.role && <span>{p.role.replace("_", " ")}</span>}
                        {p.batting_style && <span className="text-primary">{p.batting_style === "left_hand" ? "LH" : "RH"}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function TeamEditor({ value, onChange, onSave, onCancel, saving }) {
  const setField = (k, v) => onChange({ ...value, [k]: v });
  const setPlayer = (i, patch) =>
    onChange({ ...value, players: value.players.map((p, idx) => (idx === i ? { ...p, ...patch } : p)) });
  const addPlayer = () => onChange({ ...value, players: [...value.players, blankPlayer()] });
  const removePlayer = (i) => onChange({ ...value, players: value.players.filter((_, idx) => idx !== i) });

  return (
    <div className="border border-primary bg-card p-6 mb-8" data-testid="team-editor">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-black uppercase text-2xl tracking-tight">
          {value.id ? "Edit Team" : "New Team"}
        </h2>
        <button
          onClick={onCancel}
          data-testid="team-editor-cancel"
          className="p-2 text-muted-foreground hover:text-destructive border border-border hover:border-destructive"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <label className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">Team name</label>
      <input
        value={value.name}
        onChange={(e) => setField("name", e.target.value)}
        data-testid="team-name-input"
        placeholder="e.g. Mumbai Indians"
        className="w-full bg-transparent border-b border-border focus:border-primary focus:outline-none pb-2 mb-6 mt-2 font-display font-black uppercase text-2xl tracking-tight"
      />
      <div className="space-y-3">
        {value.players.map((p, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`team-editor-player-${i}`}>
            <span className="col-span-1 font-mono text-xs text-muted-foreground stat-tabular text-center">{i + 1}.</span>
            <input
              value={p.name}
              onChange={(e) => setPlayer(i, { name: e.target.value })}
              data-testid={`team-editor-name-${i}`}
              placeholder="Player name"
              className="col-span-5 bg-transparent border border-border focus:border-primary focus:outline-none px-3 py-2 text-sm font-mono"
            />
            <select
              value={p.role}
              onChange={(e) => setPlayer(i, { role: e.target.value })}
              data-testid={`team-editor-role-${i}`}
              className="col-span-2 bg-card border border-border focus:border-primary focus:outline-none px-2 py-2 text-xs font-mono uppercase"
            >
              <option value="">Role</option>
              {ROLE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <input
              value={p.jersey_number}
              onChange={(e) => setPlayer(i, { jersey_number: e.target.value })}
              data-testid={`team-editor-jersey-${i}`}
              placeholder="#"
              maxLength={6}
              className="col-span-1 bg-transparent border border-border focus:border-primary focus:outline-none px-2 py-2 text-sm font-mono text-center"
            />
            <select
              value={p.batting_style}
              onChange={(e) => setPlayer(i, { batting_style: e.target.value })}
              data-testid={`team-editor-style-${i}`}
              className="col-span-2 bg-card border border-border focus:border-primary focus:outline-none px-2 py-2 text-xs font-mono uppercase"
            >
              <option value="">Hand</option>
              {STYLE_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
            <button
              onClick={() => removePlayer(i)}
              data-testid={`team-editor-remove-${i}`}
              disabled={value.players.length <= 2}
              className="col-span-1 p-2 text-muted-foreground hover:text-destructive border border-border hover:border-destructive disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              aria-label="Remove player"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      <button
        onClick={addPlayer}
        data-testid="team-editor-add-player"
        className="text-xs uppercase tracking-[0.2em] font-bold text-primary hover:text-primary/80 inline-flex items-center gap-2 mt-4"
      >
        <Plus className="w-3 h-3" strokeWidth={3} /> Add Player
      </button>
      <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-border">
        <button
          onClick={onCancel}
          data-testid="team-editor-cancel-btn"
          className="tactical-btn border border-border hover:border-foreground px-5 py-2 text-xs"
        >
          Cancel
        </button>
        <button
          onClick={onSave}
          disabled={saving}
          data-testid="team-editor-save-btn"
          className="tactical-btn bg-primary text-primary-foreground hover:bg-[#C9E620] disabled:opacity-50 px-6 py-2 inline-flex items-center gap-2 text-xs"
        >
          <Save className="w-3.5 h-3.5" strokeWidth={3} />
          {saving ? "Saving..." : value.id ? "Update Team" : "Save Team"}
        </button>
      </div>
    </div>
  );
}
